import React, { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight,
    Sparkles,
    Shield,
    Cpu,
    LineChart,
    ArrowRight,
    Rocket,
    Cloud,
    ShieldCheck,
    Atom,
    Bot,
    Blocks,
    Layers,
} from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";
import Marquee from "../components/shared/Marquee";
import CTABanner from "../components/shared/CTABanner";
import ServicesShowreel from "../components/shared/ServicesShowreel";
import Testimonials from "../components/shared/Testimonials";
import {
    SERVICE_CATEGORIES,
    CASE_STUDIES,
    TECH_PARTNERS,
    TESTIMONIALS,
    SERVICES_SHOWREEL,
} from "../lib/data";
import { INDUSTRY_SOLUTIONS } from "../lib/industries";

const HOME_SERVICE_ICONS = {
    Rocket,
    Cloud,
    ShieldCheck,
    Sparkles,
    Cpu,
    Atom,
    Bot,
    Blocks,
    Layers,
};

const ParticleWave = lazy(() => import("../components/three/ParticleWave"));

export default function Home() {
    return (
        <main data-testid="page-home" className="relative">
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative min-h-[100vh] flex items-end overflow-hidden pb-20 md:pb-28"
            >
                <div className="absolute inset-0 -z-10 noise-bg opacity-[0.06]" />
                <div className="absolute inset-0 -z-10">
                    <Suspense fallback={null}>
                        <ParticleWave />
                    </Suspense>
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 md:px-8 lg:px-10 pt-32">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-green mb-8">
                            <Sparkles className="h-3.5 w-3.5" />
                            Aspire. Design. Execute.
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] font-bold leading-[0.95] tracking-[-0.04em]">
                            Turn ideas into
                            <br />
                            <span className="text-gradient-brand">reality at scale.</span>
                        </h1>
                        <p className="mt-7 max-w-2xl text-zinc-400 text-lg md:text-xl leading-relaxed">
                            Netfotech Solutions empowers global enterprises with
                            scalable AI driven innovation and secure digital
                            ecosystems engineered by 25+ years of combined
                            leadership.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-primary"
                                className="btn-glow group relative inline-flex items-center gap-2 rounded-full bg-brand-linear px-7 py-4 text-[15px] font-medium text-white hover:shadow-glow-brand transition-all"
                            >
                                Start a conversation
                                <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-2 rounded-full glass border border-white/10 px-7 py-4 text-[15px] font-medium text-white hover:border-white/30 transition-all"
                            >
                                Explore our services
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </motion.div>

                </div>
            </section>

            {/* TECHNOLOGY PARTNERS — highlighted ecosystem strip */}
            <section
                data-testid="trust-section"
                className="relative py-20 md:py-24 border-y border-white/5 bg-ink-900/60 overflow-hidden"
            >
                {/* Decorative gradient frame */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-green/60 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-blue/60 to-transparent" />
                <div className="absolute -left-24 top-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full bg-brand-blue/10 blur-3xl pointer-events-none" />
                <div className="absolute -right-24 top-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />

                <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 rounded-full glass-strong border border-brand-green/30 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-green">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-green animate-pulse" />
                                Technology partners
                            </div>
                            <h2 className="mt-4 font-display text-2xl md:text-3xl lg:text-[34px] font-semibold tracking-tight leading-[1.1]">
                                A  platform agnostic <span className="text-gradient-brand">ecosystem of partners.</span>
                            </h2>
                            <p className="mt-3 text-[14.5px] text-zinc-400 max-w-xl leading-relaxed">
                                Deep certified expertise across hyperscalers, AI labs, data, security and operations vendors picked for your use-case, not ours.
                            </p>
                        </div>
                        <div className="hidden md:flex items-center gap-2 text-[12px] text-zinc-500 font-mono">
                            <span className="text-brand-green">{TECH_PARTNERS.length}</span> alliance partners
                        </div>
                    </div>
                    <Marquee items={TECH_PARTNERS.map((p) => p.name)} />
                </div>
            </section>

            {/* ABOUT PREVIEW */}
            <section
                data-testid="about-preview-section"
                className="relative py-20 md:py-24"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <SectionHeading
                            eyebrow="Company Overview"
                            title={<>25+ years of leadership, one philosophy.</>}
                            subtitle="We bring decades of enterprise experience across AI, cloud, security and consulting distilled into a simple discipline: Aspire. Design. Execute."
                        />
                        <Link
                            to="/about"
                            data-testid="about-preview-cta"
                            className="mt-8 inline-flex items-center gap-2 text-brand-green hover:text-white transition-colors text-[15px] font-medium"
                        >
                            Learn about Netfotech
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {[
                            {
                                icon: Sparkles,
                                title: "Aspire",
                                desc: "We start by understanding your ambition the outcome you need in 3 years, not 3 quarters.",
                                color: "text-brand-blue",
                            },
                            {
                                icon: Cpu,
                                title: "Design",
                                desc: "Our architects design secure, scalable systems that map to business value from day one.",
                                color: "text-brand-green",
                            },
                            {
                                icon: LineChart,
                                title: "Execute",
                                desc: "Global delivery teams ship production software with relentless focus on quality and SLAs.",
                                color: "text-brand-blue",
                            },
                            {
                                icon: Shield,
                                title: "Sustain",
                                desc: "We stay, run and evolve the platforms with you through change, growth and compliance.",
                                color: "text-brand-green",
                            },
                        ].map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="glass rounded-3xl border border-white/5 p-7 brand-border-hover tracing-beam"
                                data-testid={`philosophy-${p.title.toLowerCase()}`}
                            >
                                <p.icon className={`h-6 w-6 ${p.color}`} />
                                <div className="mt-6 font-display text-2xl font-semibold tracking-tight">
                                    {p.title}
                                </div>
                                <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                                    {p.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES SHOWREEL (video) */}
            <ServicesShowreel
                video={SERVICES_SHOWREEL.video}
                poster={SERVICES_SHOWREEL.poster}
                caption={SERVICES_SHOWREEL.caption}
            />

            {/* SERVICES PREVIEW */}
            <section
                data-testid="services-preview-section"
                className="relative py-20 md:py-24 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                        <SectionHeading
                            eyebrow="What we do"
                            title="Built for now. Ready for what’s next."
                            subtitle="From AI-first transformation to quantum readiness and Web3 an integrated portfolio engineered for the decade ahead."
                        />
                        <Link
                            to="/services"
                            data-testid="services-preview-cta"
                            className="inline-flex items-center gap-2 self-start md:self-end text-brand-green hover:text-white transition text-[15px] font-medium"
                        >
                            All services
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICE_CATEGORIES.map((s, i) => {
                            const Icon = HOME_SERVICE_ICONS[s.icon] || Sparkles;
                            const isLastOnTablet = i === SERVICE_CATEGORIES.length - 1;
                            return (
                                <motion.div
                                    key={s.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.55, delay: i * 0.06 }}
                                    className={
                                        isLastOnTablet
                                            ? "md:col-span-2 md:mx-auto md:w-full md:max-w-[calc(50%-10px)] lg:col-span-1 lg:max-w-none"
                                            : ""
                                    }
                                >
                                    <Link
                                        to={`/services?category=${s.id}`}
                                        data-testid={`service-card-${s.id}`}
                                        className="group block h-full glass rounded-3xl border border-white/5 p-7 brand-border-hover tracing-beam"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 border border-white/10">
                                                <Icon className="h-5 w-5 text-brand-green" />
                                            </div>
                                            <div className="font-mono text-xs text-zinc-500">
                                                {s.num}
                                            </div>
                                        </div>
                                        <div className="mt-7 font-display text-[22px] md:text-2xl font-semibold leading-tight tracking-tight">
                                            {s.name}
                                        </div>
                                        <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                                            {s.tagline}
                                        </p>
                                        <ul className="mt-5 flex flex-wrap gap-1.5">
                                            {s.offerings.slice(0, 3).map((b) => (
                                                <li
                                                    key={b.id}
                                                    className="text-[11.5px] rounded-full border border-white/10 bg-white/[0.02] px-2.5 py-1 text-zinc-400"
                                                >
                                                    {b.title}
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="mt-6 flex items-center justify-between text-[12.5px]">
                                            <span className="text-zinc-500">
                                                {s.offerings.length} sub-practices
                                            </span>
                                            <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-brand-green group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES PREVIEW */}
            <section
                data-testid="industries-preview-section"
                className="relative py-20 md:py-24 border-t border-white/5 overflow-hidden"
            >
                <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <SectionHeading
                            eyebrow="Industries"
                            title="Built for industries that can't afford downtime."
                            subtitle="Seven mission-critical sectors. One dependable partner."
                        />
                        <Link
                            to="/industries"
                            data-testid="industries-preview-cta"
                            className="inline-flex items-center gap-2 self-start md:self-end text-brand-green hover:text-white transition text-[15px] font-medium"
                        >
                            Explore industries
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {INDUSTRY_SOLUTIONS.slice(0, 8).map((ind, i) => (
                            <motion.div
                                key={ind.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.55, delay: i * 0.05 }}
                                whileHover={{ y: -4 }}
                                className="group relative overflow-hidden rounded-3xl border border-white/10 h-[260px]"
                                data-testid={`industry-tile-${ind.id}`}
                            >
                                <Link to={`/products?industry=${ind.id}`} className="block h-full">
                                    <img
                                        src={ind.image}
                                        alt={ind.name}
                                        className="absolute inset-0 h-full w-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/60 to-transparent" />
                                    <div className="relative h-full flex flex-col justify-end p-6">
                                        <div className="text-[10.5px] uppercase tracking-[0.22em] text-brand-green mb-2">
                                            Industry / {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className="font-display text-lg font-semibold tracking-tight leading-tight">
                                            {ind.shortName}
                                        </div>
                                        <div className="mt-1.5 text-[12.5px] text-zinc-400 line-clamp-2">
                                            {ind.solutions.length} solutions · {ind.name.split("&")[0].trim()}
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CASE STUDIES PREVIEW */}
            <section
                data-testid="case-studies-preview-section"
                className="relative py-20 md:py-24 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Case Studies"
                        title="Outcomes, not promises."
                        subtitle="A sample of recent transformations delivered with measurable business impact."
                    />

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 auto-rows-[420px]">
                        {CASE_STUDIES.slice(0, 5).map((cs, i) => {
                            // Hero gets full width; others 2-up grid
                            const span = i === 0 ? "md:col-span-2 md:row-span-1" : "";
                            return (
                                <motion.article
                                    key={cs.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: i * 0.06 }}
                                    className={`group relative overflow-hidden rounded-3xl border border-white/10 ${span}`}
                                    data-testid={`case-card-${cs.id}`}
                                >
                                    <img
                                        src={cs.image}
                                        alt={cs.title}
                                        className="absolute inset-0 h-full w-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-ink-950 via-ink-950/70 to-transparent" />
                                    <div className="relative h-full flex flex-col justify-end p-7 md:p-9">
                                        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-brand-green mb-3">
                                            {cs.industry}
                                        </div>
                                        <h3 className={`font-display ${i === 0 ? "text-2xl md:text-3xl" : "text-xl md:text-[22px]"} font-semibold tracking-tight leading-tight max-w-xl`}>
                                            {cs.title}
                                        </h3>
                                        <p className="mt-3 text-zinc-300 text-[13.5px] max-w-2xl line-clamp-2">
                                            {cs.summary}
                                        </p>
                                        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2.5">
                                            {cs.metrics.slice(0, i === 0 ? 3 : 2).map((m) => (
                                                <div key={m.label}>
                                                    <div className={`font-display ${i === 0 ? "text-xl md:text-2xl" : "text-lg"} font-bold text-white`}>
                                                        {m.value}
                                                    </div>
                                                    <div className="text-[10.5px] text-zinc-400 uppercase tracking-wider">
                                                        {m.label}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>

                    <div className="mt-10 flex justify-center">
                        <Link
                            to="/case-studies"
                            data-testid="case-preview-cta"
                            className="inline-flex items-center gap-2 rounded-full glass border border-white/10 px-6 py-3 text-sm font-medium hover:border-white/30 transition"
                        >
                            All case studies
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <Testimonials items={TESTIMONIALS} />

            {/* FINAL CTA */}
            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner />
                </div>
            </section>
        </main>
    );
}
